import {
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  OnboardingConfig,
  OnboardingConfigService,
} from '@app/onboarding/services/onboarding-config.service';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { FooterButtonStates } from '@app/onboarding/components/onboarding-footer/onboarding-footer.model';
import { TranslateService } from '@ngx-translate/core';
import { OnboardingStep } from '@app/onboarding/onboarding.enums';
import { OnboardingStateService } from '@app/onboarding/services/onboarding-state.service';
import { WizardComponent } from 'angular-archwizard';
import {
  AlignedResultApi,
  ResumeResult,
  PartialUserPosition,
} from '@app/onboarding/onboarding-api.model';
import { TagsApi } from '@app/tags/tag-api.model';
import { take } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { DefaultFileUploadAdapter } from '@app/uploader/upload-section/adapters/default-file-upload.adapter';
import { UploaderService } from '@app/uploader/uploader.service';
import {
  DgxUploadErrorMessages,
  DgxUploadSectionLabels,
} from '@app/uploader/upload-section/upload-section.model';
import { NotificationType } from '@lib/fresco';

@Component({
  selector: 'dgx-resume-parsing',
  templateUrl: './resume-parsing.component.html',
  styleUrls: ['./resume-parsing.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [WizardComponent],
})
export class ResumeParsingComponent
  extends SubscriberBaseDirective
  implements OnInit
{
  @ViewChild(WizardComponent) public wizard: WizardComponent;

  public readonly NotificationType = NotificationType;

  public i18n = this.translateService.instant([
    'OnboardingFooter_PreviousStep',
    'OnboardingFooter_NextStep',
    'OnboardingFooter_SkipStep',
    'Onboarding_UploadResumeHeader',
    'Onboarding_StepUpload',
    'Onboarding_StepParse',
    'Onboarding_StepResults',
    'Core_ExperiencesDisplayName',
    'Onboarding_FoundExperienceHeader',
    'Onboarding_ReviewExperienceDescription',
    'OrgFtp_DragAndDrop',
    'Core_Delete',
    'Core_Edit',
    'Core_Retry',
  ]);
  public templatePrefix: string;
  public uploadedSkills: TagsApi.Tag[] = [];
  public uploadedJobs: PartialUserPosition[] = [];
  public currentResume$: Observable<ResumeResult> =
    this.onboardingStateService.currentResume$;
  // Api for resume upload
  public uploadTo: string = '/files/uploadresume';
  private allowedFileTypes = ['.pdf', '.doc', '.docx'];
  // file adapter for uploader component
  public fileUploaderAdapter: DefaultFileUploadAdapter =
    new DefaultFileUploadAdapter(
      this.uploaderService,
      this.uploadTo,
      5,
      this.allowedFileTypes
    );
  public fileInfo: File;
  public preprocessingCompleted: boolean = false;
  // labels for uploader component
  public labels: DgxUploadSectionLabels = {
    header: this.i18n.OrgFtp_DragAndDrop,
    allowedDescription: this.translateService.instant(
      'Uploader_DefaultLimitNotice_TypeAndSize',
      { types: this.allowedFileTypes.join(', '), size: 5 }
    ),
    deleteButton: this.i18n.Core_Delete,
    editButton: this.i18n.Core_Edit,
    retryButton: this.i18n.Core_Retry,
  };
  public errorMessages: Partial<DgxUploadErrorMessages> = {
    invalidFileType: this.translateService.instant(
      'dgImageUpload_SectionInvalidFileTypeVar',
      { translatedFileTypes: this.allowedFileTypes.join(', ') }
    ),
  };
  private footerButtonStates: FooterButtonStates;
  private visited = false;

  public get onboarding_UploadResumeDescription(): string {
    return this.translateService.instant(`Onboarding_UploadResumeDescription`, {
      orgName: this.onboardingConfigService.organizationNameInOnboarding,
    });
  }

  constructor(
    private onboardingConfigService: OnboardingConfigService,
    private onboardingStateService: OnboardingStateService,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
    private uploaderService: UploaderService
  ) {
    super();
    // On back from previus pages check to see if we have a resume first
    this.onboardingStateService.currentResume$
      .pipe(take(1))
      .subscribe((currentResume) => {
        if (currentResume) {
          this.uploadedSkills = currentResume.skills;
          this.uploadedJobs = currentResume.userPositions;
          this.fileInfo = currentResume.fileInfo;
          this.preprocessingCompleted = true;
        }
      });
  }
  public get currentStepIndex(): number {
    if (!this.wizard) {
      return 0;
    }
    return this.wizard.currentStepIndex;
  }

  public ngOnInit(): void {
    const skipUrl: string = '/getstarted/review';
    this.footerButtonStates = {
      currentStep: OnboardingStep.resume,
      disableNextStep: false,
      previousButton: this.i18n.OnboardingFooter_PreviousStep,
      nextButton: this.preprocessingCompleted
        ? this.i18n.OnboardingFooter_NextStep
        : '',
      skipButton: !this.preprocessingCompleted
        ? this.i18n.OnboardingFooter_SkipStep
        : '',
      skipUrl: skipUrl,
    };

    this.onboardingConfigService.footerButtonStateChange(
      this.footerButtonStates
    );
    this.onboardingConfigService.config.subscribe(
      (config: OnboardingConfig) => {
        this.templatePrefix = config.templatePrefix;
        let steps;
        if (this.onboardingStateService.trackerSteps) {
          steps = this.onboardingConfigService.updateActiveTrackerStep(
            this.onboardingStateService.trackerSteps,
            OnboardingStep.resume
          );
          this.onboardingStateService.updateTrackerStep(steps);
        } else {
          this.onboardingStateService.updateTrackerStep(
            this.onboardingConfigService.getTrackerSteps(
              this.templatePrefix,
              OnboardingStep.resume,
              false
            )
          );
        }
        this.cdr.detectChanges();
      }
    );
    this.onboardingStateService.currentStepChange(OnboardingStep.resume);
  }

  public setFileInfo(newFile): void {
    this.preprocessingCompleted = false;
    this.fileInfo = newFile;
  }

  public preStepChange(): void {
    // if coming back to a upload page with results then display the second step
    if (!this.visited) {
      if (this.fileInfo && this.preprocessingCompleted) {
        this.wizard.goToNextStep();
      }
      this.visited = true;
    }
  }

  public preprocessingComplete(resume: AlignedResultApi): void {
    // map skills to all be followed
    const resumeUpdate: ResumeResult = this.mapResumeResults(resume);
    this.onboardingStateService.updateResume(resumeUpdate);
    this.uploadedSkills = resumeUpdate.skills;
    this.uploadedJobs = resumeUpdate.userPositions;
    this.preprocessingCompleted = true;
    this.onboardingConfigService.footerButtonStateChange({
      ...this.footerButtonStates,
      nextButton: this.i18n.OnboardingFooter_NextStep,
      skipButton: '',
    });
    this.nextStep();
  }

  /**
   * Map the parsed resume results
   */
  private mapResumeResults(resume: AlignedResultApi): ResumeResult {
    // set all skills to following
    resume.skills.map((skill) => (skill.isFollowing = true));
    const resumeUpdate: ResumeResult = {
      userCourses: [],
      userDegrees: [],
      skills: [],
      userPositions: [],
      fileInfo: this.fileInfo,
    };
    // add courses, skills, and degrees
    resumeUpdate.userCourses = resume?.alignedResult?.alignedCourses
      ? resume.alignedResult.alignedCourses
      : [];
    resumeUpdate.userDegrees = resume?.alignedResult?.alignedDegrees
      ? resume.alignedResult.alignedDegrees
      : [];
    resumeUpdate.skills = resume?.skills ? resume.skills : [];
    // map the aligned job results into a UserPosition
    resume?.alignedResult?.alignedJobs.map((alignedJob) => {
      const position: PartialUserPosition = {
        title: alignedJob.jobTitle,
        description: alignedJob.description,
        tags: alignedJob.alignedSkills,
        organizationName: alignedJob.organization,
        hoursPerWeek: 40, // setting a default value
        // NOTE: startDate/endDate -- edit is useing the new the experience modal component,
        // if the date is `undefined` it causes an `invalid date error`.
        // When this error occurs it will not display the entire modal until a input is clicked
        // Here check the data and map to null vs undefined to remove error in experience modal
        startDate:
          alignedJob.startDate === undefined ? null : alignedJob.startDate,
        endDate: alignedJob.endDate === undefined ? null : alignedJob.endDate,
        isCurrent: !alignedJob.endDate,
        level: 'Intermediate', // setting a default value
      };
      resumeUpdate.userPositions.push(position);
    });
    return resumeUpdate;
  }

  private nextStep(): void {
    this.wizard.goToNextStep();
  }
}
